import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";

export type InteractionFeedbackKind = "success" | "error" | "info";

export interface InteractionFeedbackData {
  id: number;
  kind: InteractionFeedbackKind;
  message: string;
  title?: string;
}

interface InteractionFeedbackContextValue {
  feedback: InteractionFeedbackData | null;
  onDismiss: () => void;
  onBlocked: (message: string) => void;
}

const InteractionFeedbackContext = createContext<InteractionFeedbackContextValue>({
  feedback: null,
  onDismiss: () => undefined,
  onBlocked: () => undefined
});

const defaultTitles: Record<InteractionFeedbackKind, string> = {
  success: "这一步已完成",
  error: "这一步还需要调整",
  info: "操作已记录"
};

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info
} as const;

export function InteractionFeedbackProvider({
  feedback,
  onDismiss,
  onBlocked,
  children
}: InteractionFeedbackContextValue & { children: ReactNode }) {
  return (
    <InteractionFeedbackContext.Provider value={{ feedback, onDismiss, onBlocked }}>
      {children}
    </InteractionFeedbackContext.Provider>
  );
}

export function useInteractionFeedback() {
  return useContext(InteractionFeedbackContext);
}

export function InteractionFeedbackBanner({
  feedback,
  onDismiss,
  className = ""
}: {
  feedback: InteractionFeedbackData;
  onDismiss: () => void;
  className?: string;
}) {
  const Icon = icons[feedback.kind];
  const bannerRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const banner = bannerRef.current;
    const active = document.activeElement;
    if (active instanceof HTMLElement && !banner?.contains(active)) returnFocusRef.current = active;
    return () => {
      if (!banner?.contains(document.activeElement)) return;
      const target = returnFocusRef.current;
      window.requestAnimationFrame(() => {
        if (target?.isConnected) target.focus();
      });
    };
  }, [feedback.id]);

  useEffect(() => {
    if (feedback.kind !== "error") return;
    const frame = window.requestAnimationFrame(() => {
      const banner = bannerRef.current;
      if (!banner) return;
      const rect = banner.getBoundingClientRect();
      const scrollContainer = banner.closest<HTMLElement>(".task-panel, .audit-sheet, .quiz-screen");
      const clip = scrollContainer?.getBoundingClientRect();
      const visibleInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
      const visibleInContainer = !clip || (rect.top >= clip.top && rect.bottom <= clip.bottom);
      if (visibleInViewport && visibleInContainer) return;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      banner.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [feedback.id, feedback.kind]);

  function dismissAndRestoreFocus() {
    const target = returnFocusRef.current;
    onDismiss();
    window.requestAnimationFrame(() => {
      if (target?.isConnected) target.focus();
    });
  }

  return (
    <div
      ref={bannerRef}
      className={`interaction-feedback interaction-feedback--${feedback.kind} ${className}`}
      data-feedback-kind={feedback.kind}
      data-feedback-id={feedback.id}
      role={feedback.kind === "error" ? "alert" : "status"}
      onFocusCapture={(event) => {
        const previous = event.relatedTarget;
        if (previous instanceof HTMLElement && !bannerRef.current?.contains(previous)) returnFocusRef.current = previous;
      }}
    >
      <Icon className="interaction-feedback__icon" size={20} aria-hidden="true" />
      <div className="interaction-feedback__copy">
        <strong>{feedback.title ?? defaultTitles[feedback.kind]}</strong>
        <p>{feedback.message}</p>
      </div>
      <button type="button" onClick={dismissAndRestoreFocus} title="关闭反馈" aria-label="关闭反馈">
        <X size={16} />
      </button>
    </div>
  );
}
